/**
 * Prompt Library Hooks
 * 
 * React Query hooks for fetching and managing prompt library data.
 * Used by the Threads command bar for the Prompt Library feature.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { apiRequest } from '@/lib/queryClient';

// ============================================================================
// Types
// ============================================================================

export interface PromptTemplate {
  id: number;
  title: string;
  description: string | null;
  bodyTemplate: string;
  scope: 'system' | 'user';
  categoryId: number | null;
  categorySlug: string | null;
  categoryLabel: string | null;
}

export interface PromptCategory {
  id: number | null;
  slug: string | null;
  label: string;
  templates: PromptTemplate[];
}

export interface EffectiveTemplatesResponse {
  categories: PromptCategory[];
}

export interface PromptVariable {
  key: string;
  label: string;
  description?: string;
  example: string;
  category: 'profile' | 'business' | 'preferences';
}

export interface VariablesResponse {
  variables: PromptVariable[];
  byCategory: Record<string, PromptVariable[]>;
}

export interface CreateTemplateDto {
  title: string;
  description?: string;
  bodyTemplate: string;
  categoryId?: number;
}

export interface UpdateTemplateDto {
  title?: string;
  description?: string;
  bodyTemplate?: string;
  categoryId?: number | null;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
  isPublic?: boolean;
}

export interface UserTemplate {
  id: number;
  title: string;
  description: string | null;
  bodyTemplate: string;
  categoryId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: {
    id: number;
    label: string;
    slug: string;
  } | null;
}

export interface UserTemplatesResponse {
  items: UserTemplate[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const promptLibraryKeys = {
  all: ['prompt-library'] as const,
  effective: (userKey: string | number | null | undefined = 'anonymous') =>
    [...promptLibraryKeys.all, 'effective', userKey ?? 'anonymous'] as const,
  myTemplates: (userKey: string | number | null | undefined = 'anonymous') =>
    [...promptLibraryKeys.all, 'my-templates', userKey ?? 'anonymous'] as const,
  variables: () => [...promptLibraryKeys.all, 'variables'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch effective templates (merged system + user prompts) for the current user
 */
export function useEffectiveTemplates(userKey?: string | number | null) {
  const { user } = useAuth();
  const scopedKey = userKey ?? user?.id ?? 'anonymous';

  return useQuery<EffectiveTemplatesResponse>({
    queryKey: promptLibraryKeys.effective(scopedKey),
    queryFn: async () => {
      // apiRequest already parses JSON and returns the data
      return await apiRequest<EffectiveTemplatesResponse>('GET', '/api/prompt-library/effective-templates');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!user,
  });
}

/**
 * Fetch user's own templates
 */
export function useMyTemplates(userKey?: string | number | null) {
  const { user } = useAuth();
  const scopedKey = userKey ?? user?.id ?? 'anonymous';

  return useQuery<UserTemplatesResponse>({
    queryKey: promptLibraryKeys.myTemplates(scopedKey),
    queryFn: async () => {
      // apiRequest already parses JSON and returns the data
      return await apiRequest<UserTemplatesResponse>('GET', '/api/prompt-library/my-templates?pageSize=100');
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!user,
  });
}

/**
 * Fetch variable registry
 */
export function usePromptVariables() {
  return useQuery<VariablesResponse>({
    queryKey: promptLibraryKeys.variables(),
    queryFn: async () => {
      // apiRequest already parses JSON and returns the data
      return await apiRequest<VariablesResponse>('GET', '/api/prompt-library/variables');
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
  });
}

/**
 * Create a new user template
 */
export function useCreateMyTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const scopedKey = user?.id ?? 'anonymous';
  
  return useMutation({
    mutationFn: async (data: CreateTemplateDto) => {
      // apiRequest already parses JSON and returns the data
      return await apiRequest('POST', '/api/prompt-library/my-templates', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.myTemplates(scopedKey) });
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.effective(scopedKey) });
    },
  });
}

/**
 * Update a user template
 */
export function useUpdateMyTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const scopedKey = user?.id ?? 'anonymous';
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateTemplateDto }) => {
      // apiRequest already parses JSON and returns the data
      return await apiRequest('PATCH', `/api/prompt-library/my-templates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.myTemplates(scopedKey) });
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.effective(scopedKey) });
    },
  });
}

/**
 * Archive a user template
 */
export function useArchiveMyTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const scopedKey = user?.id ?? 'anonymous';
  
  return useMutation({
    mutationFn: async (id: number) => {
      // apiRequest already parses JSON and returns the data
      return await apiRequest('PATCH', `/api/prompt-library/my-templates/${id}/archive`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.myTemplates(scopedKey) });
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.effective(scopedKey) });
    },
  });
}

// ============================================================================
// Variable Substitution Helper (client-side)
// ============================================================================

/**
 * All possible profile values that can be substituted into prompt templates.
 * This interface should have a key for every variable in PROMPT_VARIABLE_REGISTRY.
 */
export interface UserProfileValues {
  // Profile
  advisorName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  bio?: string;
  // Business
  businessName?: string;
  agencyName?: string;
  businessType?: string;
  businessDescription?: string;
  yearsInTravel?: string;
  website?: string;
  phone?: string;
  // Social
  instagramHandle?: string;
  facebookPage?: string;
  twitterHandle?: string;
  linkedinProfile?: string;
  tiktokHandle?: string;
  youtubeChannel?: string;
  // Preferences
  destinationSpecialties?: string;
  primarySpecialties?: string;
  clientTypes?: string;
  preferredSuppliers?: string;
  languages?: string;
  certifications?: string;
  writingTone?: string;
  communicationStyle?: string;
}

/**
 * Convert a label-style variable name to camelCase key
 * e.g., "Advisor Name" -> "advisorName", "Destination Specialties" -> "destinationSpecialties"
 */
function labelToCamelCase(label: string): string {
  return label
    .split(/\s+/)
    .map((word, index) => {
      const lowerWord = word.toLowerCase();
      return index === 0 ? lowerWord : lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
    })
    .join('');
}

/**
 * Substitute {{variableKey}} tokens in a template with user profile values.
 *
 * Handles both:
 * - Standard format: {{advisorName}} (camelCase keys)
 * - Legacy format: {{Advisor Name}} (label-style with spaces)
 */
export function substituteVariables(
  template: string,
  values: UserProfileValues,
): string {
  // First pass: handle standard camelCase keys (e.g., {{advisorName}})
  let result = template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    const value = values[key as keyof UserProfileValues];
    if (value != null && value !== '') {
      return value;
    }
    // Return empty string for missing values (clean output)
    return '';
  });

  // Second pass: handle legacy format with spaces (e.g., {{Advisor Name}})
  // Pattern matches: {{Capital Letter followed by letters/spaces}}
  result = result.replace(/\{\{([A-Z][a-zA-Z ]+)\}\}/g, (match, label: string) => {
    // Convert label to camelCase key and look up
    const key = labelToCamelCase(label.trim());
    const value = values[key as keyof UserProfileValues];
    if (value != null && value !== '') {
      return value;
    }
    // Return empty string for missing values (clean output)
    return '';
  });

  return result;
}

/**
 * Profile data structure expected from the API
 */
export interface ProfileData {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  bio?: string;
  businessName?: string;
  businessType?: string;
  businessDescription?: string;
  yearsExperience?: number | null;
  experienceStartYear?: number | null;
  website?: string;
  phone?: string;
  instagramHandle?: string;
  facebookPage?: string;
  twitterHandle?: string;
  linkedinProfile?: string;
  tiktokHandle?: string;
  youtubeChannel?: string;
  destinationExpertise?: string[];
  primarySpecialties?: string[];
  clientTypes?: string[];
  preferredSuppliers?: string[];
  languages?: string[];
  certifications?: Array<{ name?: string; title?: string }>;
  writingTone?: string;
  communicationStyle?: string;
}

/**
 * Extract profile values from user data for variable substitution.
 * Maps all fields from the user profile to the corresponding variable keys
 * defined in PROMPT_VARIABLE_REGISTRY.
 */
export function extractProfileValues(
  user: { email?: string; displayName?: string } | null,
  profile?: ProfileData | null,
): UserProfileValues {
  const currentYear = new Date().getFullYear();
  let yearsInTravel = '';
  
  // Calculate years in travel (1-based: starting this year = 1 year)
  if (profile?.experienceStartYear) {
    yearsInTravel = String(currentYear - profile.experienceStartYear + 1);
  } else if (profile?.yearsExperience != null) {
    yearsInTravel = String(profile.yearsExperience);
  }
  
  // Format certifications as a comma-separated string
  const certifications = profile?.certifications
    ?.map(c => c.name || c.title || '')
    .filter(Boolean)
    .join(', ') || '';
  
  return {
    // Profile
    advisorName: profile?.displayName || user?.displayName || '',
    firstName: profile?.firstName || user?.displayName?.split(' ')[0] || '',
    lastName: profile?.lastName || '',
    email: user?.email || '',
    bio: profile?.bio || '',
    
    // Business
    businessName: profile?.businessName || '',
    agencyName: '', // Not typically available in profile, leave empty
    businessType: profile?.businessType || '',
    businessDescription: profile?.businessDescription || '',
    yearsInTravel,
    website: profile?.website || '',
    phone: profile?.phone || '',
    
    // Social
    instagramHandle: profile?.instagramHandle || '',
    facebookPage: profile?.facebookPage || '',
    twitterHandle: profile?.twitterHandle || '',
    linkedinProfile: profile?.linkedinProfile || '',
    tiktokHandle: profile?.tiktokHandle || '',
    youtubeChannel: profile?.youtubeChannel || '',
    
    // Preferences
    destinationSpecialties: profile?.destinationExpertise?.join(', ') || '',
    primarySpecialties: profile?.primarySpecialties?.join(', ') || '',
    clientTypes: profile?.clientTypes?.join(', ') || '',
    preferredSuppliers: profile?.preferredSuppliers?.join(', ') || '',
    languages: profile?.languages?.join(', ') || '',
    certifications,
    writingTone: profile?.writingTone || '',
    communicationStyle: profile?.communicationStyle || '',
  };
}

